import { inject, Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { firstValueFrom, Observable } from "rxjs";
import { EntityApiService } from "../../../../shared/store/entity-store/entity-api.service";
import { environment } from "../../../../../environments/environment";
import { QueryParam } from "../../../../shared/store/store-types.model";
import { queryParamToHttp } from "../../../../shared/store/utils/store-utils.service";
import { BonLivraison } from "../../models/bon-livraison.model";
import { TypePdf } from "../../../documents/enums/type-pdf.enum";
import { printPdf } from "../../../../shared/utils/print-pdf.utils";

@Injectable({ providedIn: "root" })
export class BlApiService implements EntityApiService<BonLivraison> {
  loadOne(id: string): Observable<BonLivraison> {
    return this.httpClient.get<BonLivraison>(
      `${environment.api()}/bonLivraisons/id/eager/${id}`,
    );
  }

  httpClient = inject(HttpClient);

  create(entity: BonLivraison): Observable<BonLivraison> {
    return this.httpClient.post<BonLivraison>(
      `${environment.api()}/bonLivraisons`,
      entity,
    );
  }

  remove(id: string): Observable<string> {
    throw new Error("Method not implemented.");
  }

  update(entity: BonLivraison): Observable<BonLivraison> {
    return this.httpClient.post<BonLivraison>(
      `${environment.api()}/bonLivraisons`,
      entity,
    );
  }

  loadAll(query?: QueryParam): Observable<BonLivraison[]> {
    let params = queryParamToHttp(query);
    return this.httpClient.get<BonLivraison[]>(
      `${environment.api()}/bonLivraisons/search`,
      { params },
    );
  }

  loadAllNotBilled(): Observable<BonLivraison[]> {
    return this.httpClient.get<BonLivraison[]>(
      `${environment.api()}/bonLivraisons/not_billed`,
    );
  }

  loadAllArchived(): Observable<BonLivraison[]> {
    return this.httpClient.get<BonLivraison[]>(
      `${environment.api()}/bonLivraisons/archived`,
    );
  }

  /**
   * Recherche paginée + filtrée pour la vue "Bons de livraison".
   * Retourne `{ data, total }`. Les BLs sont enrichis avec `palettesCount`, `poidsBrutTotal`,
   * `tiers`, `adresse`, `chargement` (+ transporteur, entrepotSource), `facture`.
   */
  loadPage(
    page: number,
    limit: number,
    query?: any,
  ): Observable<{ data: BonLivraison[]; total: number }> {
    let params = new HttpParams()
      .set("page", String(page))
      .set("pageSize", String(limit));
    if (query?.search) params = params.set("search", query.search);
    if (query?.includeArchived) params = params.set("includeArchived", "true");
    if (query?.minDate) params = params.set("minDate", query.minDate);
    if (query?.maxDate) params = params.set("maxDate", query.maxDate);
    if (query?.sortBy) params = params.set("sortBy", query.sortBy);
    if (query?.sortDir) params = params.set("sortDir", query.sortDir);
    if (query?.commandeClientId) params = params.set("commandeClientId", query.commandeClientId);
    const appendArr = (key: string, arr?: string[]) => {
      if (arr?.length) arr.forEach((v) => (params = params.append(key, v)));
    };
    appendArr("states", query?.states);
    appendArr("tiersIds", query?.tiersIds);
    appendArr("entrepotSourceIds", query?.entrepotSourceIds);
    appendArr("adresseIds", query?.adresseIds);
    appendArr("chargementIds", query?.chargementIds);
    appendArr("factureIds", query?.factureIds);
    return this.httpClient.get<{ data: BonLivraison[]; total: number }>(
      `${environment.api()}/bonLivraisons/search`,
      { params },
    );
  }

  loadBlPayload(blId: string) {
    return this.httpClient.get(
      `${environment.api()}/bonLivraisons/payload/${blId}`,
    );
  }

  /** G3 — update only the tracking number. Triggers a CONNECTOR push if
   *  the BL is in EXPEDIE so the new tracking reaches PS. */
  updateTrackingNumber(blId: string, trackingNumber: string): Observable<void> {
    return this.httpClient.post<void>(
      `${environment.api()}/bonLivraisons/${blId}/tracking`,
      { trackingNumber },
    );
  }

  sendMail(payload: any): Observable<any> {
    return this.httpClient.post(
      `${environment.api()}/bonLivraisons/send_mail`,
      payload,
    );
  }

  async printPdfBL(payload, documentTitle?: string) {
    const res = await firstValueFrom(
      this.httpClient.post(
        `${environment.pdfApi()}/pdf/${TypePdf.BL_CLIENT}`,
        payload,
        {
          responseType: "blob",
        },
      ),
    );
    const file = new Blob([res], { type: "application/pdf" });
    printPdf(URL.createObjectURL(file), documentTitle || 'BL');
  }

  /**
   * Récupère le PDF du BL et retourne un blob URL (à ouvrir dans un nouvel
   * onglet). Pas d'impression — utile depuis un dialog où l'overlay bloque
   * le focus de printJS.
   */
  async fetchPdfBLBlobUrl(payload): Promise<string> {
    const res = await firstValueFrom(
      this.httpClient.post(
        `${environment.pdfApi()}/pdf/${TypePdf.BL_CLIENT}`,
        payload,
        {
          responseType: "blob",
        },
      ),
    );
    const file = new Blob([res], { type: "application/pdf" });
    return URL.createObjectURL(file);
  }
}
